import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Space, Table, Tag, Select } from 'antd';
import useAuth from 'hooks/useAuth';
import useTenantFlags from 'hooks/useTenantFlags';
import { useAscensorActividades } from 'providers/AscensorActividadesProvider';
import { useCliente } from 'providers/Clientes/ClienteProvider';
import React, { useEffect, useState } from 'react';
import CalendarView from '../../../features/common/components/CalendarView';

const ClienteActividadesPage = ({ id }) => {
  const { data, call, openAdd, openEdit, openDelete } = useAscensorActividades();
  const { readOnly } = useTenantFlags();
  const { ascensores, loadingAscensores, callAscensores } = useCliente();
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [selectedAscensor, setSelectedAscensor] = useState(null);

  useEffect(() => {
    if (id) {
      callAscensores(id);
    }
  }, [callAscensores, id]);

  useEffect(() => {
    if (ascensores && ascensores.length > 0 && !selectedAscensor) {
      // Seleccionar el primer ascensor por defecto
      const firstAscensor = ascensores[0];
      setSelectedAscensor(firstAscensor.id);
      call(firstAscensor.id);
    }
  }, [ascensores, selectedAscensor, call]);

  useEffect(() => {
    if (selectedAscensor) {
      call(selectedAscensor);
    }
  }, [call, selectedAscensor]);

  // Function to get month names from the month numbers and sort them
  const getMonthNames = (months) => {
    const monthNames = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ];
    return months
      .sort((a, b) => a - b)
      .map((month) => monthNames[month - 1])
      .join(', ');
  };

  // Transform data to include month names for each activity
  const transformedData = data?.map((activity) => ({
    ...activity,
    meses: getMonthNames(activity?.Fechas?.map((fecha) => fecha.mes)),
  }));

  // Get unique categories for the filter options
  const uniqueCategories = [...new Set(data?.map((activity) => activity.Categoria.nombre))];

  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };
  const { user } = useAuth();

  const handleAscensorChange = (value) => {
    setSelectedAscensor(value);
  };

  return (
    <div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Seleccionar Ascensor:</label>
        <Select
          style={{ width: 300 }}
          placeholder="Seleccionar ascensor"
          value={selectedAscensor}
          onChange={handleAscensorChange}
          loading={loadingAscensores}
        >
          {ascensores?.map((ascensor) => (
            <Select.Option key={ascensor.id} value={ascensor.id}>
              {ascensor.nombre}
              {ascensor.requiere_plan === false ? ' · sin plan' : ''}
            </Select.Option>
          ))}
        </Select>
      </div>

      <CalendarView data={data ?? []} />
      {user.is_ejecutivo && !readOnly && (
        <div className="flex justify-between items-center mb-2">
          <div></div>
          <Space>
            {/*<Button*/}
            {/*  onClick={() => openImport(id)}*/}
            {/*  type="default"*/}
            {/*  icon={<PlusOutlined />}*/}
            {/*>*/}
            {/*  Importar*/}
            {/*</Button>*/}
            <Button
              onClick={() => openAdd(selectedAscensor, () => call(selectedAscensor))}
              type="primary"
              icon={<PlusOutlined />}
              disabled={!selectedAscensor}
            >
              Agregar
            </Button>
          </Space>
        </div>
      )}

      <Table
        dataSource={transformedData}
        pagination={false}
        rowKey={'id'}
        size="small"
        onChange={handleTableChange}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => a.id - b.id,
            sortOrder: sortedInfo.columnKey === 'id' && sortedInfo.order,
            defaultSortOrder: 'descend',
          },
          {
            title: 'Título',
            dataIndex: 'title',
            key: 'title',
          },
          {
            title: 'Categoría',
            dataIndex: 'Categoria',
            key: 'Categoria',
            filters: uniqueCategories.map((category) => ({
              text: category,
              value: category,
            })),
            filteredValue: filteredInfo.Categoria || null,
            onFilter: (value, record) => record.Categoria.nombre.includes(value),
            render: (categoria) => (
              <div className="text-center">
                <Tag color="blue">{categoria.nombre}</Tag>
              </div>
            ),
          },
          {
            title: 'Descripción',
            dataIndex: 'actividad',
            key: 'actividad',
          },
          {
            title: 'Meses',
            dataIndex: 'meses',
            key: 'meses',
          },
          {
            title: 'Obligatorio',
            dataIndex: 'obligatorio',
            key: 'obligatorio',
            render: (obligatorio) => (
              <div className="text-center">
                <Tag color={obligatorio ? 'green' : 'yellow'}>{obligatorio ? 'Sí' : 'No'}</Tag>
              </div>
            ),
            width: 100,
          },
          {
            title: 'Acciones',
            key: 'acciones',
            hidden: !user.is_ejecutivo || readOnly,
            render: (_, record) => (
              <div className="flex justify-end space-x-2">
                <Button
                  onClick={() => openEdit(record, () => call(selectedAscensor))}
                  type="primary"
                  icon={<EditOutlined />}
                >
                  Editar
                </Button>
                <Button
                  onClick={() =>
                    openDelete(record.id, selectedAscensor, () => call(selectedAscensor))
                  }
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                >
                  Eliminar
                </Button>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default ClienteActividadesPage;
